/**
 * GHÉP MẢNH + TRANG BỊ SKILL + SHOP KIM CƯƠNG — LOGIC THUẦN, KHÔNG import Phaser.
 *
 * Vì sao nằm ở `sim/`: đây là LUẬT CHƠI (trừ mảnh, thêm chiêu, xếp ô) chứ không phải
 * giao diện. Nhờ vậy test được bằng số đo — `tests/craft.test.ts` chạy không cần đồ hoạ.
 *
 * Cách cắm vào game: state là `MetaState` thật (chỉ cần có thêm 4 trường
 * `diamonds` · `shards` · `craftedSkills` · `skillLoadout`). `ensureProgression()`
 * được gọi trong migrate save cũ để save thiếu trường vẫn chơi được.
 *
 * ⚠️ BẪY 1 — KHÔNG import `sim/meta.ts` từ đây dù rất muốn dùng `HERO_NAMES`:
 *   1) `HERO_NAMES` là `const` RIÊNG TƯ trong meta.ts (không export) ⇒ không lấy được
 *      mà không sửa file đó (bị cấm trong nhiệm vụ này).
 *   2) meta.ts SẼ import ngược file này để gọi `ensureProgression()` lúc migrate
 *      ⇒ import hai chiều thành vòng. Vòng import trong ESM không chết ngay nhưng
 *      `CLASSES` có thể còn `undefined` lúc eval ⇒ lỗi chỉ hiện ở một số thứ tự import.
 *   ⇒ Tự giữ danh sách tên ở đây (chép đúng tên của meta.ts để UI không thấy lạ).
 *
 * ⚠️ BẪY 2 — MỌI hàm đều gọi `ensureProgression(state)` trước khi làm gì. Save cũ có
 * thể thiếu `shards`/`skillLoadout` ⇒ đọc thẳng `state.shards.skill[x]` là **ném
 * TypeError**, mà yêu cầu là "dữ liệu lạ chỉ trả ok:false, không ném". Chuẩn hoá ở
 * một chỗ rồi mọi hàm khác yên tâm.
 *
 * ⚠️ BẪY 3 — Ô trang bị KHÔNG được để LỖ HỔNG. `splice` thì mảng tự nén (tốt), nhưng
 * `loadout[5] = x` khi mảng mới dài 2 sẽ tạo `[a, b, <3 ô trống>, x]` — UI vẽ ra 3 ô
 * rỗng và `validateLoadout` báo "Có ô trống". `equipSkill` vì thế LẤP ô trống bằng
 * chiêu gốc chưa dùng trước khi đặt chiêu mới.
 */
import { CLASSES, type SkillDef } from '../data/classes';
import { DIAMOND_SHOP, shopItemById, type ShopItem, type ShopKind } from '../data/diamond-shop';
import {
  EXTRA_SKILLS,
  MAX_EQUIPPED_SKILLS,
  classOfSkill,
  craftableSkills,
  defaultLoadout,
  skillById,
  skillPool,
} from '../data/skills-extra';
import type { ClassId } from './types';

/**
 * Trạng thái tối thiểu module cần — `MetaState` thật thoả mãn cấu trúc này.
 *
 * Cố ý khai theo CẤU TRÚC (structural) chứ không `extends MetaState`: như vậy
 * `sim/craft.ts` không phụ thuộc `sim/meta.ts`, tránh import vòng, mà save cũ chỉ cần
 * `ensureProgression()` là khớp.
 */
export interface CraftState {
  diamonds: number;
  shards: { hero: Record<string, number>; skill: Record<string, number> };
  /** v7: ba trường dưới thay cho `heroes[]` — SỔ TƯỚNG ĐÃ XOÁ (xem `sim/meta.ts`). */
  unlocked: ClassId[];
  classShards: Partial<Record<ClassId, number>>;
  classPower: Partial<Record<ClassId, number>>;
  craftedSkills: Record<string, string[]>;
  skillLoadout: Record<string, string[]>;
}

/** Giá ghép: 30 mảnh tướng · 25 mảnh chiêu. (Phụ kiện đã XOÁ HẲN — xem `MAX_ROSTER`.) */
export const SHARD_COST: { hero: number; skill: number } = { hero: 30, skill: 25 };

/**
 * SỔ NHÂN VẬT TỐI ĐA — CHÉP từ `sim/meta.ts` (xem BẪY 1 ở đầu file: import ngược file đó sẽ
 * thành vòng import). `tests/craft.test.ts` khoá hai hằng số này bằng nhau.
 */
export const MAX_ROSTER = 5;

/** 5 class — khai tại chỗ để không phụ thuộc giá trị module lúc eval. */
/**
 * MỌI CLASS HỢP LỆ — 7 class (5 gốc + 2 MỚI: Thợ mộc · Thợ lặn).
 *
 * ⚠️ LỖI ĐÃ SỬA: chỗ này từng là 5 class gốc ⇒ `isKnownClassId('carpenter')` trả FALSE, nên
 * `sanitizeClassMap` NUỐT mảnh của 2 class mới (đo được: `classShards.carpenter = 30` → mất),
 * và `buyShopItem` từ chối bán túi mảnh cho chúng ⇒ **không có cách nào mở khoá 2 class mới**.
 * Danh sách khai TẠI CHỖ (không mượn `CLASS_ORDER`) để tránh import vòng — xem BẪY 1 ở đầu file.
 */
const ALL_CLASSES: ClassId[] = ['strength', 'agility', 'street', 'army', 'mother', 'carpenter', 'diver'];


// ------------------------------------------------------------------ tiện ích nội bộ

interface ClassDefLike {
  name: string;
  skills: Array<{ id: string; name: string }>;
}

function classDef(classId: string): ClassDefLike | undefined {
  return (CLASSES as unknown as Record<string, ClassDefLike | undefined>)[classId];
}

/** Chiêu này có phải chiêu GỐC của class (luôn sở hữu) không. */
function isOriginal(def: ClassDefLike, id: string): boolean {
  return def.skills.some((s) => s.id === id);
}

/** Đã sở hữu chiêu: chiêu gốc LUÔN sở hữu; chiêu mới phải nằm trong `craftedSkills`. */
function ownsSkill(state: CraftState, classId: ClassId, id: string): boolean {
  const def = classDef(classId);
  if (!def) return false;
  if (isOriginal(def, id)) return true;
  return (state.craftedSkills[classId] ?? []).includes(id);
}

/** Đếm số chiêu gốc còn trong bộ — dùng cho luật "không bỏ chiêu gốc cuối cùng". */
function countOriginals(def: ClassDefLike, loadout: string[]): number {
  return loadout.filter((id) => isOriginal(def, id)).length;
}

/** Đếm an toàn cho dữ liệu cũ: số rác / âm / NaN ⇒ 0. */
function safeCount(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
}

function sanitizeMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (v && typeof v === 'object') {
    for (const [k, n] of Object.entries(v as Record<string, unknown>)) out[k] = safeCount(n);
  }
  return out;
}


export function craftSkill(state: CraftState, classId: ClassId, skillId: string): { ok: boolean; message: string } {
  ensureProgression(state);
  const def = classDef(classId);
  if (!def) return { ok: false, message: `Không có class: ${classId}` };
  const sk = craftableSkills(classId).find((s) => s.id === skillId);
  if (!sk) {
    // Phân biệt rõ 2 ca để người chơi biết mình sai ở đâu.
    if (isOriginal(def, skillId)) {
      return { ok: false, message: `${skillId} là chiêu gốc — đã có sẵn, không cần ghép` };
    }
    return { ok: false, message: `Không có chiêu ${skillId} cho ${def.name}` };
  }
  const owned = state.craftedSkills[classId] ?? (state.craftedSkills[classId] = []);
  if (owned.includes(skillId)) return { ok: false, message: `Đã ghép ${sk.name} rồi` };
  const have = state.shards.skill[classId] ?? 0;
  if (have < SHARD_COST.skill) {
    return { ok: false, message: `Thiếu mảnh chiêu ${def.name}: cần ${SHARD_COST.skill}, đang có ${have}` };
  }
  state.shards.skill[classId] = have - SHARD_COST.skill;
  owned.push(skillId);
  return { ok: true, message: `Ghép được ${sk.name} cho ${def.name}` };
}

// ------------------------------------------------------------------ trang bị skill

/**
 * TRANG BỊ 1 CHIÊU VÀO Ô `index` (0..6) — thay ô đang có, không cho trùng chiêu.
 *
 * Luật "không bỏ chiêu gốc CUỐI CÙNG" áp cho cả `equipSkill` (thay thế) lẫn
 * `unequipSkill` (bỏ hẳn): nếu chỉ chặn lúc bỏ thì người chơi cứ thay dần 7 chiêu gốc
 * bằng 7 chiêu ghép là lách được luật, và bộ trang bị mất luôn "chiêu gốc" làm mốc.
 */
export function equipSkill(
  state: CraftState,
  classId: ClassId,
  skillId: string,
  index: number,
): { ok: boolean; message: string } {
  ensureProgression(state);
  const def = classDef(classId);
  if (!def) return { ok: false, message: `Không có class: ${classId}` };
  if (!Number.isInteger(index) || index < 0 || index >= MAX_EQUIPPED_SKILLS) {
    return { ok: false, message: `Ô ${index} không hợp lệ (chỉ có ô 0..${MAX_EQUIPPED_SKILLS - 1})` };
  }
  const sk = skillById(skillId);
  if (!sk || !skillPool(classId).some((s) => s.id === skillId)) {
    return { ok: false, message: `Chiêu ${skillId} không thuộc ${def.name}` };
  }
  if (!ownsSkill(state, classId, skillId)) {
    return { ok: false, message: `Chưa sở hữu ${sk.name} — phải ghép mảnh trước` };
  }
  const loadout = state.skillLoadout[classId] ?? (state.skillLoadout[classId] = defaultLoadout(classId));
  const at = loadout.indexOf(skillId);
  if (at >= 0) {
    return { ok: false, message: `${sk.name} đã nằm ở ô ${at + 1}` };
  }
  // Thay 1 chiêu gốc cuối cùng bằng chiêu ghép ⇒ chặn (xem doc ở đầu hàm).
  const replaced = loadout[index];
  if (!isOriginal(def, skillId) && replaced !== undefined && isOriginal(def, replaced) && countOriginals(def, loadout) <= 1) {
    return { ok: false, message: 'Không thể bỏ chiêu gốc cuối cùng của class' };
  }
  /*
   * LẤP Ô TRỐNG trước khi đặt (xem BẪY 3): chỉ lấp bằng CHIÊU GỐC chưa dùng để không
   * "tặng" chiêu ghép mà người chơi chưa ghép.
   *
   * ⚠️⚠️ ĐỆM TỪ `skillPool()` (10 CHIÊU), KHÔNG từ `defaultLoadout()` (3 chiêu).
   *
   * LỖI TIỀM ẨN teammate `skill-cap-test` phát hiện: `defaultLoadout()` nay chỉ trả
   * `MAX_EQUIPPED_SKILLS` = **3** chiêu (trước là 7) ⇒ nếu lấp từ đó thì khi trần nâng lên 4+,
   * vòng lặp hết chiêu đệm và `break` sớm ⇒ **bộ chiêu còn LỖ HỔNG `undefined`** (đúng BẪY 3 mà
   * comment này cảnh báo). Với trần 3 hiện tại thì `defaultLoadout` vẫn đủ nên lỗi CHƯA lộ ra —
   * nhưng đó là bom hẹn giờ. `skillPool()` luôn đủ (7 gốc + 3 ghép), và ta vẫn chỉ lấy CHIÊU GỐC
   * bằng cách lọc theo `CLASSES[classId].skills`.
   */
  const baseIds = new Set((CLASSES[classId]?.skills ?? []).map((sk) => sk.id));
  while (loadout.length < index) {
    const pad = skillPool(classId)
      .map((sk) => sk.id)
      .find((id) => baseIds.has(id) && !loadout.includes(id));
    if (!pad) break; // không xảy ra với pool 10 chiêu, nhưng đừng để vòng lặp vô tận
    loadout.push(pad);
  }
  if (loadout.length === index) loadout.push(skillId);
  else loadout[index] = skillId;
  return { ok: true, message: `${def.name} trang bị ${sk.name} vào ô ${index + 1}` };
}

/**
 * BỎ CHIÊU Ở Ô `index` — phải giữ ≥1 chiêu, và không bỏ chiêu gốc cuối cùng.
 * Mảng nén lại bằng `splice` nên KHÔNG để lỗ hổng (không có ô `undefined`).
 */
export function unequipSkill(state: CraftState, classId: ClassId, index: number): { ok: boolean; message: string } {
  ensureProgression(state);
  const def = classDef(classId);
  if (!def) return { ok: false, message: `Không có class: ${classId}` };
  const loadout = state.skillLoadout[classId] ?? (state.skillLoadout[classId] = defaultLoadout(classId));
  if (!Number.isInteger(index) || index < 0 || index >= loadout.length) {
    return { ok: false, message: `Ô ${index} không có chiêu nào` };
  }
  if (loadout.length <= 1) {
    return { ok: false, message: 'Phải giữ tối thiểu 1 chiêu' };
  }
  const id = loadout[index];
  if (isOriginal(def, id) && countOriginals(def, loadout) <= 1) {
    return { ok: false, message: 'Không thể bỏ chiêu gốc cuối cùng của class' };
  }
  loadout.splice(index, 1);
  // Van an toàn: không bao giờ để bộ rỗng (dù luật trên đã chặn).
  if (loadout.length === 0) loadout.push(...defaultLoadout(classId));
  const sk = skillById(id);
  return { ok: true, message: `${def.name} bỏ ${sk?.name ?? id} khỏi ô ${index + 1}` };
}

// ------------------------------------------------------------------ shop kim cương

function isKnownClassId(id: string | undefined): id is ClassId {
  return !!id && ALL_CLASSES.includes(id as ClassId);
}

/**
 * MUA 1 MÓN TRONG SHOP BẰNG KIM CƯƠNG.
 *
 * Thứ tự BẮT BUỘC: kiểm tra hiệu lực → mới trừ 💎 → mới áp dụng. Nếu trừ tiền trước
 * rồi mới phát hiện "đã sở hữu" thì người chơi mất kim cương mà chẳng nhận được gì.
 */
export function buyShopItem(state: CraftState, itemId: string): { ok: boolean; message: string } {
  ensureProgression(state);
  const item = shopItemById(itemId);
  if (!item) return { ok: false, message: `Shop không có món: ${itemId}` };
  if (state.diamonds < item.cost) {
    return { ok: false, message: `Không đủ kim cương (cần ${item.cost}, đang có ${state.diamonds})` };
  }

  switch (item.kind) {
    /*
     * MẢNH CLASS (v7): mua bằng 💎, cộng vào `classShards`. 30 mảnh MỞ KHOÁ class, 10 mảnh NÂNG CẤP
     * tướng; bán lại ra vàng 20/ mảnh ở Túi đồ. Chấp nhận cả class CHƯA mở khoá — đó chính là cách
     * người chơi gom đủ mảnh để mở (người dùng: *"bao gồm cả mảnh của class đã và chưa unlock"*).
     */
    case 'shardClass': {
      const key = item.target ?? '';
      if (!key || !isKnownClassId(key)) return { ok: false, message: `Món ${item.id} thiếu target hợp lệ` };
      const amount = item.amount ?? 0;
      if (amount <= 0) return { ok: false, message: `Món ${item.id} thiếu số lượng mảnh` };
      state.diamonds -= item.cost;
      state.classShards[key] = (state.classShards[key] ?? 0) + amount;
      return { ok: true, message: `Nhận ${amount} mảnh class ${classDef(key)?.name ?? key} — còn ${state.diamonds}💎` };
    }

    case 'shardSkill': {
      const key = item.target ?? '';
      if (!key) return { ok: false, message: `Món ${item.id} thiếu target` };
      const amount = item.amount ?? 0;
      if (amount <= 0) return { ok: false, message: `Món ${item.id} thiếu số lượng mảnh` };
      state.diamonds -= item.cost;
      state.shards.skill[key] = (state.shards.skill[key] ?? 0) + amount;
      return { ok: true, message: `Nhận ${amount} mảnh chiêu (${key}) — còn ${state.diamonds}💎` };
    }

    case 'skill': {
      const skillId = item.target ?? '';
      const owner = classOfSkill(skillId);
      if (!owner) return { ok: false, message: `Không có chiêu: ${skillId}` };
      // Chỉ bán chiêu MỚI. Mua "chiêu gốc" là vô nghĩa (ai cũng có sẵn) ⇒ chặn để
      // không ai mất 260💎 oan.
      if (!(EXTRA_SKILLS[owner] ?? []).some((s) => s.id === skillId)) {
        return { ok: false, message: `${skillId} là chiêu gốc — không bán trong shop` };
      }
      const owned = state.craftedSkills[owner] ?? (state.craftedSkills[owner] = []);
      if (owned.includes(skillId)) return { ok: false, message: `Đã có ${skillId} rồi` };
      state.diamonds -= item.cost;
      owned.push(skillId);
      const sk = skillById(skillId);
      return { ok: true, message: `Mua ${sk?.name ?? skillId} cho ${classDef(owner)?.name ?? owner} — còn ${state.diamonds}💎` };
    }

    default: {
      // Nhánh này chỉ chạy nếu ai đó thêm `ShopKind` mới mà quên xử lý.
      const never: never = item.kind;
      return { ok: false, message: `Loại món chưa hỗ trợ: ${String(never)}` };
    }
  }
}

// ------------------------------------------------------------------ migrate save

/**
 * BẢO ĐẢM STATE CÓ ĐỦ TRƯỜNG — gọi trong migrate save cũ, và ở đầu mọi hàm trên.
 *
 * Việc phải làm (theo đúng yêu cầu):
 *  · `craftedSkills` / `skillLoadout` có ĐỦ 5 class;
 *  · loadout mặc định = 7 CHIÊU GỐC khi trống;
 *  · bỏ id LẠ, bỏ chiêu ghép CHƯA SỞ HỮU, bỏ id TRÙNG, cắt về ≤7;
 *  · luôn giữ ≥1 chiêu VÀ ≥1 chiêu gốc.
 *
 * Idempotent: gọi 2 lần không nhân đôi gì (mọi bước đều là hợp nhất/ khử trùng).
 */
/**
 * LÀM SẠCH map theo class (`classShards` · `classPower`): chỉ nhận khoá là class HỢP LỆ và giá trị
 * số hữu hạn ≥ 0. Khoá lạ bị bỏ (save rác không làm hỏng UI Túi đồ).
 */
function sanitizeClassMap(v: unknown): Partial<Record<ClassId, number>> {
  const out: Partial<Record<ClassId, number>> = {};
  if (!v || typeof v !== 'object') return out;
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
    if (!isKnownClassId(k)) continue;
    const num = typeof n === 'number' && Number.isFinite(n) ? Math.max(0, n) : 0;
    out[k] = num;
  }
  return out;
}

export function ensureProgression(state: CraftState): void {
  const raw = state as unknown as {
    diamonds?: unknown;
    shards?: unknown;
    // ⚠️ KHÔNG khai `heroes` ở đây nữa: v7 đã xoá sổ tướng, và nếu còn nhánh đọc `raw.heroes` thì
    // `ensureProgression()` sẽ dựng lại `state.heroes = []` ⇒ mỗi lần `saveMeta()` ghi rác
    // `"heroes":[]` trở lại localStorage (đúng lỗi test-updater đo được).
    craftedSkills?: unknown;
    skillLoadout?: unknown;
  };

  state.diamonds = safeCount(raw.diamonds);

  const shards = (raw.shards && typeof raw.shards === 'object' ? raw.shards : {}) as Record<string, unknown>;
  state.shards = {
    hero: sanitizeMap(shards.hero),
    skill: sanitizeMap(shards.skill),
  };

  /*
   * v7: KHÔNG còn `heroes`. Ba trường dưới đây phải LUÔN tồn tại và SẠCH, nếu không thì save mới
   * lại mọc lại `heroes` rác (đúng thứ `migrateMeta()` vừa `delete`) và UI Túi đồ đọc `undefined`.
   *
   * ⚠️⚠️ HỢP NHẤT, KHÔNG GHI ĐÈ — đây là lỗi thật đã đo được:
   * `migrateMeta()` dựng `classShards` từ mảnh cũ RỒI mới gọi `ensureProgression(state)`. Nếu ở đây
   * gán `state.classShards = sanitize(raw.classShards)`, thì biến `raw` CHÍNH LÀ `state` đã có mảnh
   * — nghe thì vô hại, NHƯNG `sanitizeClassMap` chỉ giữ khoá class hợp lệ, và bản trước còn lọc
   * bằng `CLASS_ORDER` (5 class) ⇒ mảnh của `carpenter`/`diver` bị NUỐT (đo: `carpenter: 30` →
   * `0`). Nay: giữ nguyên giá trị ĐANG CÓ, chỉ chuẩn hoá số; khoá lạ bị bỏ.
   */
  const rawUnlocked = Array.isArray((raw as { unlocked?: unknown }).unlocked) ? ((raw as { unlocked: unknown[] }).unlocked) : [];
  const keepUnlocked = state.unlocked ?? [];
  state.unlocked = [...new Set([...keepUnlocked, ...rawUnlocked.filter((c): c is ClassId => typeof c === 'string' && isKnownClassId(c))])];
  /*
   * ⚠️ LỖI ĐÃ SỬA (test-updater đo được): bản trước viết
   *     { ...sanitizeClassMap(raw.classShards), ...(state.classShards ?? {}) }
   * và vì `raw === state`, vế SAU (giá trị CHƯA làm sạch) ghi đè vế TRƯỚC (đã làm sạch) ⇒
   * `sanitizeClassMap` VÔ HIỆU. Đo được: `{agility: NaN, street: -5}` lọt nguyên vào state ⇒
   * Túi đồ hiện "NaN mảnh", số mảnh âm, `sellClassShards` từ chối sai.
   * Nay làm sạch trực tiếp trên `state` — vẫn KHÔNG nuốt mảnh của class mới vì `sanitizeClassMap`
   * dùng `isKnownClassId` (7 class).
   */
  state.classShards = sanitizeClassMap(state.classShards);
  state.classPower = sanitizeClassMap(state.classPower);

  const craftedRaw = (raw.craftedSkills && typeof raw.craftedSkills === 'object' ? raw.craftedSkills : {}) as Record<string, unknown>;
  const loadoutRaw = (raw.skillLoadout && typeof raw.skillLoadout === 'object' ? raw.skillLoadout : {}) as Record<string, unknown>;

  const crafted: Record<string, string[]> = {};
  const loadouts: Record<string, string[]> = {};

  for (const c of ALL_CLASSES) {
    const def = classDef(c);
    if (!def) continue;
    const dfl = defaultLoadout(c);
    const poolIds = new Set(skillPool(c).map((s) => s.id));
    const extraIds = new Set((EXTRA_SKILLS[c] ?? []).map((s) => s.id));

    // ---- chiêu đã ghép: chỉ giữ chiêu MỚI của ĐÚNG class, khử trùng
    const craftedList = Array.isArray(craftedRaw[c]) ? (craftedRaw[c] as unknown[]) : [];
    crafted[c] = [...new Set(craftedList.filter((id): id is string => typeof id === 'string' && extraIds.has(id)))];

    // ---- bộ trang bị: lọc id hợp lệ + đã sở hữu, khử trùng, cắt ≤7
    const rawList = Array.isArray(loadoutRaw[c]) ? (loadoutRaw[c] as unknown[]) : [];
    const usable: string[] = [];
    for (const id of rawList) {
      if (typeof id !== 'string' || !poolIds.has(id)) continue; // id lạ
      if (!crafted[c].includes(id) && !dfl.includes(id)) continue; // chiêu ghép chưa sở hữu
      if (usable.includes(id)) continue; // trùng
      usable.push(id);
    }
    let list = usable.slice(0, MAX_EQUIPPED_SKILLS);
    if (list.length === 0) list = [...dfl];
    // Luôn phải còn ≥1 CHIÊU GỐC làm mốc: thiếu thì chèn chiêu gốc đầu tiên vào đầu bộ.
    if (!list.some((id) => dfl.includes(id))) {
      list = [dfl[0], ...list].slice(0, MAX_EQUIPPED_SKILLS);
    }
    loadouts[c] = list;
  }

  state.craftedSkills = crafted;
  state.skillLoadout = loadouts;
}

// ------------------------------------------------------------------ tiện cho lớp render/ UI

/** Bộ chiêu ĐANG TRANG BỊ dưới dạng `SkillDef` — world/ HUD dùng để niệm theo thứ tự ô. */
export function equippedSkills(state: CraftState, classId: ClassId): SkillDef[] {
  ensureProgression(state);
  const ids = state.skillLoadout[classId] ?? defaultLoadout(classId);
  return ids.map((id) => skillById(id)).filter((s): s is SkillDef => !!s);
}

/** Món shop theo loại — UI vẽ tab không phải tự lọc. */
export function shopItemsOfKind(kind: ShopKind): ShopItem[] {
  return DIAMOND_SHOP.filter((i) => i.kind === kind);
}
